/* Data for scenario C5CA phase IE 
This data structure holds all the information to create the slides to present during the IE phase.
It mirrors the tree structure that is presented in the Excel file. All nodes in the tree are called actions whether 
they are assessments or interventions. Subactions are the children of the actions in the tree.
Fields:
id: identifies the action-node in the tree. It is a string since it is unique.
label: text for the action as it will appear on the slide
type: indicates whether the action is Required, Not indicated, Optional, Alternative.
    Required: students are asked if they performed it.
    Not indicated: it will be listed on a list of not indicated actions that the student performed accompanied by 
    the feedback on the feedbackErrors field.
    Optional: it will be accepted as correct if performed.
    Alternative: will be used for those interventions that can be performed as alternative to required ones.
    [
    Not used for the pilot study but was used for trial 1:
    Contraindicated: it will be listed under a list of contraindicated actions that the student performed
    accompanied by the feedback on the feedbackErrors field.
    Unnecessary: it will be listed under a list of unnecessary actions that the student performed
    accompanied by the feedback on the feedbackErrors field.
    Irrelevent: it will be listed under a list of irrelevant actions that the student performed
    accompanied by the feedback on the feedbackErrors field.
    ]
AlternativeToIntv: indicates the intervention for which the current one is an alternative. It will only have a value if
the type is Alternative.
feedbackAbsent: feedback given if the action was no performed. Each element in the array corresponds to a paragraph.
feedbackOutOfOrder: feedback given if the action was done late (e.g., if the action is Scene size-up and it was done 
    after Primary Survey). Each element in the array corresponds to a paragraph.
feedbackErrors: feedback if the action was performed incorrectly (e.g., wrong ventilation rate). Each element in the array
 corresponds to a paragraph.
examineIfAbsent: boolean to indicate if this Action needs to be examined when it is Absent (phase E)
examineIfErrors: boolean to indicate if this Action needs to be examined when there are performance errors (phase E)
promptsIfAbsent: prompts to be included in question headers in phase E, to hint on how to reflect when Action was Absent
promptsIfErrors: prompts to be included in question headers in phase E, to hint on how to reflect when Action was performed incorrectly
subActionsList: boolean that indicates whether the subActions need to be listed as bullet points on the slide. If they
    don't need to be listed it is usually because they have been grouped however, the subActions still need to be 
    included in the subActions field because they are used by the analysis to give a summary of their status.
subActions: array with the corresponding subactions (to form the tree)
*/

import { ACTION_TYPES, WAIT_TIFF, WAIT_SANDY, SUM_NODE_NO_FEEDBACK, NO_FEEDBACK, SUM_NODE_FBCK_CHILDREN} from "./constants";

export const B5CA_PhaseIE = {
    Action: {
      id: "top",
      label: "fully assess and manage the patient in this scenario", /* the way it will appear on the slide */
      type: ACTION_TYPES.REQ, /*indicates whether the action was Required, Contraindicated, Unnecessary, or Irrelevant" */
      feedbackAbsent: ["Your log indicates you didn't complete any of these assessment or managment goal. Please restart the scenario and try again"], /*feedback if the action was not performed*/
      feedbackOutOfOrder: [""], /*feedback if the action was done late (e.g., if the action is Scene Size-up 
        and it was done after Primary Survey)*/
      feedbackErrors: [""], /*feedback if the action was performed incorrectly (e.g. wrong Ventilation rate)*/
      examineIfAbsent: false, /* boolean to indicate if this Action needs to be examined (phase E) */
      examineIfErrors: false,
      promptsIfAbsent: "", /* prompts to the included in question in phase E to hint on how to reflect */
      promptsIfErrors: "",
      subActionsList: true, /* boolean that indicates if subActions need to be listed as bullet points on slide */
      subActions: [ /*array with the corresponding subactions (to form the tree)*/
        {
          id: "scene-size-up",
          label: "Put on BSI and do a complete Scene Size-up before entering the scene",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself.","Your scene size up gives you a big-picture view of what is going on before you even begin examining the patient and includes determining the mechanism of injury and number of patients, requesting additional help if necessary, and considering stabilization of the spine.","In this case, you would have learned that this patient was involved in a bar fight and stabbed in the chest with a knife, which should prompt you to think about potential intrathoracic injuries, such as a pneumothorax or an injury to the aorta or other major vessel."],
          feedbackOutOfOrder: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself.",  "Your scene size up also gives you a big-picture view of what is going on before you even begin examining the patient. This includes determining the mechanism of injury and number of patients, requesting additional help if necessary, and considering stabilization of spine."],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "required-action-BSI",
                label: "Apply BSI",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "assess-scene-safety",
                label: "Determine the scene/situation is safe",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Your first steps should have been to don your protective wear, including eye protection, and to ensure the scene is safe. You cannot assist a patient if you are not safe yourself."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "assess-injury-mechanism",
                label: "Determine the mechanism of injury (MOI) or nature of illness (NOI)",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Knowing what caused the patient's injury or illness can allow you to start developing a plan of action before you even assess your patient.", "In this case, you would have learned that this patient was involved in a bar fight and stabbed in the chest with a knife, which should prompt you to think about potential intrathoracic injuries, such as a pneumothorax or an injury to the aorta or other major vessel."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "assess-num-patients",
                label: "Determine the number of patients",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Usually you will have one patient, but if there is more than one you will need to plan accordingly."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-requests-additional-help",
                label: "Request additional help if necessary",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Consider extra help based on number of patients, mechanism of injury, or your patient's status."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-considers-stabilize-spine",
                label: "Consider stabilization of spine",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["All trauma patients are at risk for spinal injury. Take this into consideration before initial contact with the patient to prevent unintentional injury. While a stab wound to the chest may not be high-risk, if you don't know everything that happened during the altercation, you should have erred on the side of caution."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
          ],
        },
        {
          id: "primary-survey",
          label: "Perform a Primary Survey and manage all identified life threats",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["The primary survey is an assessment of mental status, life-threats, and the patient's ABCs. It is where you will identify and address the patient's most serious injuries.", "In this case, the Primary Survey would have revealed a patient who is conscious but is having trouble breathing. Your primary survey does not reveal any apparently life threats so you can move forward to the next step of your assessment."],
          feedbackOutOfOrder: ["You should have started your Primary Survey after the Scene Size-up and completed it before starting the Secondary Survey. Only interrupt your assessment to control life-threats: (1) Conditions that comprimise a patent airway (2) Conditions that compromise breathing or respirations, such as a tension pneumothorax, (3) Conditions that compromise circulation, such as severe bleeding, (4) Cardiac arrest, and any  other potentially life threatening injuries or conditions.  Your patient's condition will deteriorate if these conditions are not addressed before continuing on with your assessment and history taking."],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "general-impression",
                label: "Conduct an Initial Assessment",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["You should have performed an Initial Assessment: gathered a general impression, identified any apparent life threats, and determined your patient's level of consciousness.  Your Initial Assessment would have revealed a patient who is conscious but having respiratory distress."],
                feedbackOutOfOrder: ["While you may be tempted to jump straight into your ABCs, apparent life threats (i.e., uncontrolled bleeding) should be addressed first, as they can cause a patient to deteriorate quickly. Also, be sure to assess the patient's level of consciousness as an unconscious patient will need a pulse check first to ensure he or she does not require CPR."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assess-patient-condition",
                        label: "State or request a general impression",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Your general impression allows you to start developing a plan of action before you even assess your patient.", "In this case, your general impression would have been of a man stabbed in the chest with a knife."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assess-loc",
                        label: "Assess the patient's level of consciousness",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The patient's level of consciousness alters how you progress through your assessment.", "The patient is conscious, but with a GCS of 13 (eyes closed, responds to verbal stimuli)."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assess-life-threats",
                        label: "Determine the chief complaint/apparent life-threats",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["If you had determined apparent life threats, you would have noticed that the patient was conscious without apparent immediate life threats."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "intv-position-patient",
                label: "Place patient in supine position",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["This patient presents leaning forward at a bar. You and your partner should carefully place him in supine position in order to examine him. Due to hypotension, he should remain in supine position except while you examine his posterior during the secondary survey."],
                feedbackOutOfOrder: ["You should have placed the patient in supine position before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "Manage a potential head or spinal injury", /* optional intervention because subActions are optional*/
                label: "Manage a potential head or spinal injury",
                type: ACTION_TYPES.OPT,
                feedbackAbsent: ["Part of managing a head and/or spinal injury includes manual stabilization of the cervical spine before beginning your assessment, as movement may further injure the patient. While this patient may not have an obvious injury to the head or spine, you want to always stabilize the spine before you begin your assessment. As you continue your assessment, you may decide he no longer needs spinal stabilization, but you will never be wrong to err on the side of caution. A cervical collar will help you maintain stability while you continue with your exam."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent:"", /*Did not consider risk for cervical spine injury; considered cervical spine injury but did not think the mechanism necessitated spinal stabilization*/
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                  {
                    id: "intv-spinal-immobilization-technique-manual-c-spine", /* optional intervention */
                    label: "Apply (or direct a partner to apply) manual c-spine stabilization",
                    type: ACTION_TYPES.OPT,
                    feedbackAbsent: ["Part of managing a head and/or spinal injury includes manual stabilization of the cervical spine before beginning your assessment, as movement may further injure the patient. While this patient may not have an obvious injury to the head or spine, you want to always stabilize the spine before you begin your assessment. As you continue your assessment, you may decide he no longer needs spinal stabilization, but you will never be wrong to err on the side of caution."],
                    feedbackOutOfOrder: ["During trauma situations it is necessary to stabilize the c-spine before continuing as not doing so may cause further injury to the patient."],
                    feedbackErrors: [""],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: [],
                  },
                  {
                    id: "intv-spinal-immobilization-technique-cervical-collar", /* optional intervention */
                    label: "Apply an appropriately sized cervical collar",
                    type: ACTION_TYPES.OPT,
                    feedbackAbsent: ["A cervical collar will help you maintain stability while you continue with your exam."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: [""],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: [],
                  },
                ],
              },
              {
                id: "airway",
                label: "Assess the patient's airway",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Be sure to assess the airway for patency and intact physical structures.", "You would have found this patient's airway to be patent, as evidenced by the fact that he can speak to you."],
                feedbackOutOfOrder: ["If your patient is conscious, you should examine the airway before breathing and circulation. If a patient is unconscious, you should assess the pulse first to evaluate for possible cardiac arrest, then go through your ABCs."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                      {
                        id: "airway-is-open",
                        label: "Check if the airway is patent",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The first step of your airway assessment is to see if the patient's airway is open. If the patient is speaking normally, like in this patient, it is open. Signs like stridor or gasping may indicate an airway obstruction."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "airway-has-intact-physical-structures",
                        label: "Check if the airway has intact physical structures",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Though this patient did not have any facial trauma, be sure to look for injuries such as gun-shot wounds or facial fractures, as these may affect what basic or advanced airways you can use.  Avoid using a nasopharyngeal airway in patients with a suspected basilar skull fracture or nasal injury."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-open-airway-method-head-tilt", /*not indicated intervention */
                        label: "Head tilt chin lift",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's airway is patent, as is evident by the fact that he is talking--for example, complaining about pain and difficulty breathing.  If his condition deteriorates and you have reason to suspect that his airway may be compromised, you should check the  airway while maintaining the cervical spine: use a modified jaw thrust maneuver instead of a head tilt chin lift."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-open-airway-method-modified-jaw-thrust", /* optional intervention */
                        label: "Open the patient's airway with a jaw thrust maneuver",
                        type: ACTION_TYPES.OPT,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: ["You should have performed a jaw thrust maneuver before starting the secondary survey because all life-threatening conditions should be managed before starting the secondary survey."],
                        feedbackErrors: ["This patient's airway is patent, as is evident by the fact that he is talking--for example, complaining about pain and difficulty breathing.  If his condition deteriorates and you have reason to suspect that his airway may be compromised, you should check the  airway while maintaining the cervical spine: use a modified jaw thrust maneuver instead of a head tilt chin lift."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-airway-patency-technique-suction-airway", /* not indicated intervention */
                        label: "Suction the patient's mouth using a Yankauer (rigid)  or Long multi-use catheter",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's airway was patent. There is no need to suction."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-manual-finger-sweep", /* not indicated intervention */
                        label: "Manual (finger sweep)",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["If there is a visible foreign body in the mouth, it should be removed. However, a blind finger sweep is contraindicated as it could lodge a foreign body deeper into the airway. Further, there is no evidence of foreign body in this patient's mouth."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-magill-forceps-assisted", /* not indicated intervention */
                        label: "Magill forceps assisted removal",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["If there is a visible foreign body in the mouth, it should be removed. However, there is no evidence of a foreign body in this patient's mouth."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-heimlich-maneuver", /* not indicated intervention */
                        label: "Heimlich Maneuver",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["You have no evidence the patient is choking on an object so the Heimlich maneuver is not appropriate at this time."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-back-blows-and-chest-thrusts", /* not indicated intervention */
                        label: "Back blows and chest thrusts",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["You have no evidence the patient is choking on an object so back blows and chest thrusts are not appropriate at this time."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-oropharyngeal-airway", /* not indicated intervention */
                        label: "Oropharyngeal airway (OPA)",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient requires only a nasal canula or non-rebreather mask. An airway adjunct is unnecessary and would likely be refused by this conscious patient."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-nasopharyngeal-airway", /* not indicated intervention */
                        label: "Nasopharyngeal airway (NPA)",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient requires only a nasal canula or non-rebreather mask. An airway adjunct is unnecessary and would likely be refused by this conscious patient."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-orotracheal-intubation", /* not indicated intervention */
                        label: "Orotracheal intubation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's respiratory status can be stabilized with oxygen administered via a nasal canula or non-rebreather mask. More invasive airway support is unnecessary and dangerous."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        promptsIfErrors: "Thought this patient needed to be intubated",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-nasotracheal-intubation", /* not indicated intervention */
                        label: "Nasotracheal intubation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's respiratory status can be stabilized with oxygen administered via a nasal canula or non-rebreather mask. More invasive airway support is unnecessary and dangerous."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "Thought this patient needed to be intubated",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-insert-advanced-airway", /* not indicated intervention */
                        label: "Insert advanced/rescue airway",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's respiratory status can be stabilized with oxygen administered via a nasal canula or non-rebreather mask. More invasive airway support is unnecessary and dangerous."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        examineIfAbsent: "",
                        promptsIfErrors: "Thought this patient needed to be intubated",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-rapid-sequence-intubation", /* not indicated intervention */
                        label: "Rapid-sequence intubation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["RSI is not in the paramedic's scope of practice in Pennsylvania. This patient's respiratory status can be stabilized with oxygen administered via a nasal canula or non-rebreather mask. More invasive airway support is unnecessary and dangerous."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "Thought this patient needed to be intubated",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-sedation-assisted-intubation", /* not indicated intervention */
                        label: "Sedation-assisted intubation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's respiratory status can be stabilized with oxygen administered via a nasal canula or non-rebreather mask. More invasive airway support is unnecessary and dangerous."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "Thought this patient needed to be intubated",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-needle-cricothyrotomy", /* not indicated intervention */
                        label: "Needle cricothyrotomy",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["A cricothrotomy is indicated when there is an upper airway obstruction that cannot be removed. This patient has a patent airway."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "thought the patient had an upper airway obstruction",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-surgical-cricothyrotomy", /* not indicated intervention */
                        label: "Surgical cricothyrotomy",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["A cricothyrotomy is indicated when there is an upper airway obstruction that cannot be removed. This patient has a patent airway."],
                        examineIfAbsent: false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "thought the patient had an upper airway obstruction",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "breathing",
                label: "Assess the patient's breathing",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["After assessing the patient's airway, assess their breathing by listening for breath sounds and observing their respiratory rate, rhythm, and effort.", "You would have found tachypnea, shallow breaths, with absent sounds on the right, suggesting possible pneumothorax or hemothorax."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "breathing-checks-rate",
                        label: "rate",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A significantly elevated or depressed respiratory rate can suggest impending respiratory failure.", "This patient's tachypnea suggests the need for some respiratory support."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "breathing-checks-rhythm",
                        label: "rhythm",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Although not found in this case, a patient who is breathing irregularly needs ventilatory support."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "breathing-checks-quality",
                        label: "quality",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Sounds like stridor, crackles, or wheezing will help you diagnose and manage your patient's respiratory problems.", "This patient had absent breath sounds on the right, suggesting a possible pneumothorax or hemothorax."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "Manage inadequate breathing",
                label: "Manage inadequate breathing",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["The patient is taking rapid, shallow breaths. He is diminished on the L with an SpO2 of 88%. These are signs that this patient has a pneumothorax, possibly even a tension pneumothorax, and requires respiratory support."],
                feedbackOutOfOrder: ["Problems with ventilation and oxygenation are life threatening and should be addressed before you move on to your secondary survey. Repeat your exam and check vitals to ensure your interventions have worked as you expected. If not, your patient may deteriorate as you continue on with your examination."],
                feedbackErrors: [""],
                examineIfAbsent: true,
                examineIfErrors: false,
                promptsIfAbsent: "thought the patient had a simple pneumothora; didn't realize the patient had a pneumothorax; didn't know the best way to manage the patient's tension pneumothorax",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "intv-non-rebreather-mask-or-nasal-cannula",
                        label: "administering concentrated oxygen via a non-rebreather mask or nasal cannula",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The patient's respiratory rate is fast, but he is ventilating adequately. His SpO2, however, is low, suggesting inadequate oxygenation. You can use either a nasal canula or non-rebreather mask to help with oxygenation. Note that you are not assisting the patient with ventilation. In the case of a pneumothorax, you should avoid positive pressure ventilation, if possible, as this could increase the amount of air outside of the lungs, turning a pneumothorax into a tension pneumothorax."],
                        feedbackOutOfOrder: ["Oxygen administration should be performed during the primary survey as this intervention falls under 'Breathing' in the ABC's."],
                        feedbackErrors: ["This patient should be allowed to breathe independently. O2 flow rate should be adjusted to achieve an SpO2 between 95 and 99%."],
                        examineIfAbsent: true,
                        examineIfErrors: false,
                        promptsIfAbsent: "didn't realize that the patient's respiratory status was threatened; thought this patient needed ventilatory support; did not recognize the possibility of a pneumothorax",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                            {
                                id: "intv-supplemental-oxygen-device-non-rebreather-mask",
                                label: "administer concentrated oxygen via a non-rebreather mask",
                                type: ACTION_TYPES.REQ,
                                feedbackAbsent: ["The patient's respiratory rate is fast, but he is ventilating adequately. His SpO2, however, is low, suggesting inadequate oxygenation. You can use either a nasal canula or non-rebreather mask to help with oxygenation. Note that you are not assisting the patient with ventilation. In the case of a pneumothorax, you should avoid positive pressure ventilation, if possible, as this could increase the amount of air outside of the lungs, turning a pneumothorax into a tension pneumothorax."],
                                feedbackOutOfOrder: ["Oxygen administration should be performed during the primary survey as this intervention falls under 'Breathing' in the ABC's."],
                                feedbackErrors: ["This patient should be allowed to breathe independently. O2 flow rate should be adjusted to achieve an SpO2 between 95 and 99%."],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                              {
                                id: "intv-supplemental-oxygen-device-nasal-cannula",
                                label: "administer concentrated oxygen via a nasal cannula",
                                type: ACTION_TYPES.ALT,
                                alternativeToIntv: "intv-supplemental-oxygen-device-non-rebreather-mask",
                                feedbackAbsent: ["The patient's respiratory rate is fast, but he is ventilating adequately. His SpO2, however, is low, suggesting inadequate oxygenation. You can use either a nasal canula or non-rebreather mask to help with oxygenation. Note that you are not assisting the patient with ventilation. In the case of a pneumothorax, you should avoid positive pressure ventilation, if possible, as this could increase the amount of air outside of the lungs, turning a pneumothorax into a tension pneumothorax."],
                                feedbackOutOfOrder: ["Oxygen administration should be performed during the primary survey as this intervention falls under 'Breathing' in the ABC's."],
                                feedbackErrors: ["This patient should be allowed to breathe independently. O2 flow rate should be adjusted to achieve an SpO2 between 95 and 99%."],
                                examineIfAbsent: false,
                                examineIfErrors: false,
                                promptsIfAbsent: "",
                                promptsIfErrors: "",
                                subActionsList: false,
                                subActions: [],
                              },
                        ],
                      },
                      {
                        id: "intv-pleural-decompression",
                        label: "identify that this patient has a tension pneumothorax and perform pleural decompression appropriately by inserting a large bore needle between ribs 2 and 3 mid-clavicular or between ribs 5 and 6 midaxillary on the right side of the chest",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient had absent breath sounds after being stabbed with a knife to the chest. Your differential diagnosis includes pneumothorax or hemothorax. The patient also has signs of diminished cardiac output, including his hypotension and poor perfusion (pale, diaphoretic skin, and weak distal pulses) suggest that he may have a tension pneumothorax, which necessitates needle decompression. Other signs of a tension pneumothorax, which this patient did not have, include JVD or tracheal deviation."],
                        feedbackOutOfOrder: ["Tension pneumothorax is an injury that affects breathing.  It is a potentially life threatening condition that requires prompt intervention. For these reasons, it should be addressed during the primary survey."],
                        feedbackErrors: ["Once you have identified a tension pneumothorax, decompression is the appropriate next step. You should use a large bore needle (ideally 14 or 16 gauge and at least 2 inches in length) and insert it into either:", "- the 2nd or 3rd intercostal space at the mid-clavicular line", "- the 4th or 5th intercostal space at the mid-axillary line", "Remember to insert the needle above the rib, not below, because there is a neurovascular bundle that runs below each rib you want to avoid damaging."],
                        examineIfAbsent: true,
                        examineIfErrors: false,
                        promptsIfAbsent: "thought the patient had a simple pneumothorax; didn't realize the patient had a pneumothorax; didn't know the best way to manage the patient's tension pneumothorax",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-occlusive-dressing",
                        label: "Apply an occlusive dressing over the stab wound to prevent air from getting into the chest",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["An open chest wound, such as one caused by a stab-wound after the object has been pulled out, may lead to a worsening tension pneumothorax. As the patient inhales, creating negative intrathoracic pressure, air may be sucked in through the wound and collect in the intrapleural cavity. An occlusive dressing, taped on 3 sides to allow air to escape, will protect against this."],
                        feedbackOutOfOrder: ["Tension pneumothorax is an injury that affects breathing.  It is a potentially life threatening condition that requires prompt intervention. For these reasons, it should be addressed during the primary survey."],
                        feedbackErrors: [""],
                        examineIfAbsent: true,
                        examineIfErrors: false,
                        promptsIfAbsent: "didn't realize the patient had a pneumothorax; didn't know the best way to manage the patient's tension pneumothorax; thought that needle decompression would suffice",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "reassess-AB",
                        label: "Check that breathing management interventions are working by listening for a rush of air, listening for bilateral lung sounds, and requesting relevant vitals--in particular, BP, P, R, and SpO2",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["When you decide your patient needs a treatment, you must ensure your treatment had the intended outcomes. In this case, after performing a needle decompression, you will listen for a rush of air and look for improvement in the patient's exam: is he breathing more comfortably, do you hear breath sounds bilaterally? You will also assess his vitals: how do his HR, RR, BP, and SpO2 look? If your intervention does not yield the expected results, ask yourself: was my diagnosis correct? Was my intervention correct? What else should I consider?"],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                            id: "request-vitals-BP",
                            label: "vital BP",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["When you decide your patient needs a treatment, you must ensure your treatment had the intended outcomes. In this case, after performing a needle decompression, you will listen for a rush of air and look for improvement in the patient's exam: is he breathing more comfortably, do you hear breath sounds bilaterally? You will also assess his vitals: how does his BP look? If your intervention does not yield the expected results, ask yourself: was my diagnosis correct? Was my intervention correct? What else should I consider?"],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "request-vitals-Spo2",
                            label: "check Spo2",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["When you decide your patient needs a treatment, you must ensure your treatment had the intended outcomes. In this case, after performing a needle decompression, you will listen for a rush of air and look for improvement in the patient's exam: is he breathing more comfortably, do you hear breath sounds bilaterally? You will also assess his vitals: how does his SpO2 look? If your intervention does not yield the expected results, ask yourself: was my diagnosis correct? Was my intervention correct? What else should I consider?"],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "request-vitals-R",
                            label: "check R",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["When you decide your patient needs a treatment, you must ensure your treatment had the intended outcomes. In this case, after performing a needle decompression, you will listen for a rush of air and look for improvement in the patient's exam: is he breathing more comfortably, do you hear breath sounds bilaterally? You will also assess his vitals: how does his RR look? If your intervention does not yield the expected results, ask yourself: was my diagnosis correct? Was my intervention correct? What else should I consider?"],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "request-vitals-P",
                            label: "check P",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["When you decide your patient needs a treatment, you must ensure your treatment had the intended outcomes. In this case, after performing a needle decompression, you will listen for a rush of air and look for improvement in the patient's exam: is he breathing more comfortably, do you hear breath sounds bilaterally? You will also assess his vitals: how does his HR look? If your intervention does not yield the expected results, ask yourself: was my diagnosis correct? Was my intervention correct? What else should I consider?"],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                        ],
                      },
                      {
                        id: "intv-ventilation-technique-bag-valve-mask", /* not indicated intervention */
                        label: "BVM assisted ventilation",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient's respiratory status can be stabilized with oxygen administered via a nasal cannula or non-rebreather mask. More invasive airway support is unnecessary and dangerous, unless the patient refuses a mask or it is ineffective."],
                        examineIfAbsent:false,
                        examineIfErrors: true,
                        promptsIfAbsent: "",
                        promptsIfErrors: "thought this patient needed ventilatory support in addition to oxygenation",
                        subActionsList: false,
                        subActions: [],
                      },
                      
                ],
              },
              {
                id: "circulation",
                label: "Assess the patient's circulation",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Evaluate the patient's pulse and skin to assess the patient for shock.  Be sure to also perform a gross blood sweep.", "You would have noticed that your patient had a very weak radial pulse, suggesting that he may be in shock."],
                feedbackOutOfOrder: ["All unconscious patients should have a pulse check before starting the ABCs. If the patient is pulseless, you will start high-quality CPR immediately."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                  {
                    id: "pulse-checks",
                    label: "check pulse",
                    type: ACTION_TYPES.REQ,
                    feedbackAbsent: ["First check for a radial pulse. However, if you don't feel one, you want to check a carotid pulse.", "This patient's radial pulses were absent, but he did have a weak carotid pulse, which would prompt you to worry about shock.", "Checking this patient's pulse rate would have revealed that he was tachycardic. A significantly elevated or depressed pulse rate can suggest shock or respiratory failure.", "While this patient's heart had a regular rhythm, remember that an irregular rhythm can suggest impending cardiac arrest or arrhythmia.", "A patient in shock may have weak or absent pulses distally, such as this patient, suggestive of shock."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: [""],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: true,
                    subActions: [
                      {
                        id: "prompt-to-specify-pulse",
                        label: "check the appropriate pulse(s)",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["When checking for a pulse, check distally first. If you don't detect distal pulses (for example, a radial pulse), check centrally next.", "If you checked his radial pulses, you would have appreciated a pulse, though it was weak."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["If you have established that a patient has peripheral pulses, even if weak, you do not need to check for a central pulse."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "checks-pulse-rate-rhythm-quality",
                        label: "check pulse rate, rhythm, and quality",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Checking this patient's pulse rate would have revealed that he was tachycardic. A significantly elevated or depressed pulse rate can suggest shock or respiratory failure.", "While this patient's heart had a regular rhythm, remember that an irregular rhythm can suggest impending cardiac arrest or arrhythmia.", "A patient in shock may have weak or absent pulses distally, such as this patient, suggestive of shock."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                            id: "checks-pulse-rate",
                            label: "rate",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["Checking this patient's pulse rate would have revealed that he was tachycardic. A significantly elevated or depressed pulse rate can suggest shock or respiratory failure."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "checks-pulse-rhythm",
                            label: "rhythm",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["While this patient's heart had a regular rhythm, remember that an irregular rhythm can suggest impending cardiac arrest or arrhythmia."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "checks-pulse-quality",
                            label: "quality",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["A patient in shock may have weak or absent pulses distally, such as this patient, suggestive of shock."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                        ],
                      },
                    ],
                  },
                      {
                        id: "checks-skin",
                        label: "checks skin",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Pallor, which this patient had, and cyanosis are signs of inadequate oxygenation and may be caused by shock or respiratory failure.", "While cold skin suggests decompensated shock, a patient with warm skin, like this patient, may be hemodynamically stable or they may be in compensated shock.", "This patient had diaphoretic skin, which in this case is non-specific, but should prompt you to think about shock."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: true,
                        subActions: [
                          {
                            id: "checks-skin-color",
                            label: "color",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["Pallor, which this patient had, and cyanosis are signs of inadequate oxygenation and may be caused by shock or respiratory failure."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "checks-skin-temperature",
                            label: "temperature",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["While cold skin suggests decompensated shock, a patient with warm skin, like this patient, may be hemodynamically stable or they may be in compensated shock."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                          {
                            id: "checks-skin-condition",
                            label: "condition",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["This patient had diaphoretic skin, which in this case is non-specific, but should prompt you to think about shock."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: [""],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [],
                          },
                        ],
                      },
                      {
                        id: "assess-major-bleeding",
                        label: "Perform a gross blood sweep",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A gross blood sweep, which was negative in this patient, will help you identify any life-threatening bleeding you may have missed earlier when forming your general impression."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-control-severe-bleeding-technique-direct-pressure", /* not indicated intervention */
                        label: "Direct pressure",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["The patient did not have significant bleeding."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-control-severe-bleeding-technique-tourniquet", /* not indicated intervention */
                        label: "Tourniquet",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["The patient did not have significant bleeding."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-control-severe-bleeding-technique-2nd-tourniquet", /* not indicated intervention */
                        label: "Second tourniquet",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["The patient did not have significant bleeding."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-control-severe-bleeding-technique-pack-wound-with-gauze", /* not indicated intervention */
                        label: "Pack wound with gauze or hemostatic gauze",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["The patient did not have significant bleeding."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-control-severe-bleeding-technique-pressure-bandage", /* not indicated intervention */
                        label: "Pressure dressing",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["The patient did not have significant bleeding."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      /*{
                        id: "intv-control-severe-bleeding-technique-load-and-go", /* not indicated intervention
                        label: "Load and go due to uncontrolled bleeding.",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["The patient did not have uncontrolled bleeding or other life threats that could not be stabilized on site."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },*/
                ],
              },
              {
                id: "transport-decision",
                label: "Make appropriate transport decisions based on your findings so far",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Your ABCs and evaluation for life threats and level of consciousness will dictate where you transfer your patient.", "Because of the patient's respiratory distress and weak pulses, you decide to transfer the patient immediately to the nearby level II trauma center."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "decision-is-load-and-go",
                        label: "Determine that this is a critical patient",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient has a tension pneumothorax with hypotension and a penetrating chest injury. This is a patient who could deteriorate quickly so you want to make sure he gets to a hospital as soon as possible."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["This patient has a tension pneumothorax with hypotension and a penetrating chest injury. This is a patient who could deteriorate quickly so you want to make sure he gets to a hospital as soon as possible."],
                        examineIfAbsent: true,
                        examineIfErrors: false,
                        promptsIfAbsent: "didn't realize this was a critically ill patient; felt this patient should be further stabilized on site prior to transfer",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-transport",
                        label: "Decide to transport this patient to the Level 2 trauma center by ground ambulance",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient should be evaluated at the level II trauma center 7 minutes away. He should be transported by ambulance."],
                        feedbackOutOfOrder: ["Initial transport decisions should be made following the 'Primary Survey' (ABCs) because that is when the provider decides whether the patient can be further stabilized on scene or taken directly to a hospital in a 'load and go' scenario. The secondary assessment is only performed on scene if the situation is NOT a 'load and go.' Making transport decisions early is especially important if you are calling for an air ambulance to account for the time it takes for them to arrive."],
                        feedbackErrors: ["This patient should be evaluated at the level II trauma center 7 minutes away. He should be transported by ambulance."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "intv-call-for-air-ambulance", /* not indicated intervention */
                        label: "Call for air ambulance",
                        type: ACTION_TYPES.NOTIND,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["Air ambulance was not available."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      }, 
                ],
              },
              {
                id: "trauma-expose",
                label: "Trauma expose the patient",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Exposure is necessary to properly inspect the patient for injuries. Only the area being inspected should be uncovered and then recovered to prevent heat loss and hypothermia."],
                feedbackOutOfOrder: ["It is important to expose your patient to ensure that your secondary survey can be performed efficiently -- especially in trauma scenarios where there may be more unknown injuries. Exposing the patient allows the provider to more carefully examine the patient during assessment."],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [{
                  id: "exposes-body-parts",
                  label: "Exposed (and recovered) anatomical areas as necessary",
                  type: ACTION_TYPES.REQ,
                  feedbackAbsent: ["Exposure is necessary to properly inspect the patient for injuries. Only the area being inspected should be uncovered and then recovered to prevent heat loss and hypothermia."],
                  feedbackOutOfOrder: ["It is important to expose your patient to ensure that your secondary survey can be performed efficiently -- especially in trauma scenarios where there may be more unknown injuries. Exposing the patient allows the provider to more carefully examine the patient during assessment."],
                  feedbackErrors: [""],
                  examineIfAbsent: false,
                  examineIfErrors: false,
                  promptsIfAbsent: "",
                  promptsIfErrors: "",
                  subActionsList: false,
                  subActions: [],
              },],
              },
          ],
        },
        {
          id: "history-taking",
          label: "Obtain or direct a partner to obtain a patient history",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["Vital signs will help you decide how to manage your patient, as will a history. Even if you cannot obtain a history from the patient directly, you should still try to get as much information as you can (e.g., from a bystander)."],
          feedbackOutOfOrder: [""],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "required-action-obtains-vitals",
                label: "Baseline vital signs",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Baseline vitals are critical; they will help you assess, manage, and continue to monitor your patient.", "In this scenario, you would have found that this patient was tachycardic, tachypneic, and hypoxic on room air, prompting you to think about possible causes for these symptoms (such as pain, pneumothorax, hemothorax, shock)."],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["Baseline vitals are critical; they will help you assess, manage, and continue to monitor your patient. At the minimum, you should have obtained BP, P, R, and SpO2."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-attempt-obtain-sample",
                label: "SAMPLE (if possible)",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Take an efficient but thorough history from the patient or the witnesses: symptoms, allergies, medications, past medical history, last oral intake, events preceding.", "You would have learned that the patient was stabbed in the R chest after having had beer, most recently 20 minutes ago."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "required-action-obtain-OPQRST",
                label: "OPQRST (if possible)",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Assess the patient's pain: in this case the patient is not able to tell you, but you should have considered getting more information from bystanders if necessary."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
          ],
        },
        {
          id: "secondary-assessment",
          label: "Perform a Secondary Survey (rapid trauma assessment)",
          type: ACTION_TYPES.REQ,
          feedbackAbsent:["The Secondary Survey is a thorough exam of your patient to ensure you have a complete understanding of what is going on and how you need to manage your patient."],
          feedbackOutOfOrder: [""],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "head",
                label: "examine the patient's head",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["You would have noted a 1.5 inch laceration on the R side of the patient's head, above his ear.", "Inspecting the face for bruising and palpating for facial deformity may indicate facial fractures.", "Abnormal eye movement or pupils, which this patient fortunately did not have, suggest intracranial injury and should prompt you to rapidly transfer your patient to a trauma center. Additionally, bruising around the eyes may be caused by basilar skull fracture, though this aspect of the exam was normal in this patient.", "Bleeding or CF from the ear may be indicative of a basilar skull fracture. Additionally, although not found in this case, bruising behind the ear suggests a basilar skull fracture (Battle's sign).", "This patient had no drainage from the nose but consider CSF leak from a basilar skull fracture if you see clear drainage.", "If the patient had had secretions or blood in the mouth, you would have needed to apply suction to clear the airway. Additionally, any visualized foreign bodies in the mouth should be removed. A blind finger sweep is never indicated, as you could lodge a foreign body deeper into the airway. There were no obstructions in this patient’s mouth."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-head-injury",
                        label: "Check head for injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["If you had checked the skull, you would have noted a 1.5 inch laceration on the R side of the patient's head, above his ear."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-inspects-facial-bones",
                        label: "Inspect and palpate the facial bones",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Inspecting the face for bruising and palpating for facial deformity may indicate facial fractures."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-eyes",
                        label: "Inspect the eyes for PERRLA and bruising (\"raccoon eyes\")",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Abnormal eye movement or pupils, which this patient fortunately did not have, suggest intracranial injury and should prompt you to rapidly transfer your patient to a trauma center. Additionally, bruising around the eyes may be caused by basilar skull fracture, though this aspect of the exam was normal in this patient."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {id: "inspects-eyes-PERRLA",
                           feedbackAbsent: ["Abnormal eye movement or pupils, which this patient fortunately did not have, suggest intracranial injury and should prompt you to rapidly transfer your patient to a trauma center."], 
                           subActions: []},
                          {id: "inspects-eyes-racoon",
                           feedbackAbsent: ["This aspect of the exam was normal in this patient, but bruising around the eyes may be caused by basilar skull fracture."], 
                           subActions: []}
                        ],
                      },
                      {
                        id: "inspects-ears",
                        label: "Inspect the ears for blood, other fluids, and bruising (\"battle signs\")",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Although not found in this case, bleeding or CF from the ear or bruising behind the ear may suggest a basilar skull fracture (Battle's sign)."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {id: "inspects-ears-blood",
                           feedbackAbsent: ["Although not found in this case, bleeding or CF from the ear may be indicative of a basilar skull fracture"],
                           subActions: []},
                          {id: "inspects-ears-bruising",
                           feedbackAbsent: ["Although not found in this case, bruising behind the ear suggests a basilar skull fracture (Battle's sign)."], 
                           subActions: []}
                        ],
                      },
                      {
                        id: "inspects-nose",
                        label: "Inspect the nose for blood and other fluids",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient had no drainage from the nose but consider CSF leak from a basilar skull fracture if you see clear drainage."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-mouth",
                        label: "Inspect the mouth for blood, other secretions, and obstructions (teeth, foreign objects, etc.)",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["If the patient had had secretions or blood in the mouth, you would have needed to apply suction to clear the airway.", "Additionally, any visualized foreign bodies in the mouth should be removed. A blind finger sweep is never indicated, as you could lodge a foreign body deeper into the airway. There were no obstructions in this patient’s mouth."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "neck",
                label: "examine the patient's neck",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["This patient's neck exam was normal, but remember, tracheal deviation suggests unequal intrathoracic pressure (for example, a pneumothorax). The trachea will be deviated away from the side with increased pressure.", "This patient's neck exam was normal, but remember, JVD suggests that the heart is not adequately pushing out blood. Consider obstructive processes like a tension pneumothorax or hemothorax.", "This patient's neck exam was normal, but remember, a palpable step-off suggests a fracture."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-neck-injury",
                        label: "injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-tracheal-deviation",
                        label: "tracheal deviation",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient's neck exam was normal, but remember, tracheal deviation suggests unequal intrathoracic pressure (for example, a pneumothorax). The trachea will be deviated away from the side with increased pressure."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-JVD",
                        label: "jugular venous distension (JVD)",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient's neck exam was normal, but remember, JVD suggests that the heart is not adequately pushing out blood. Consider obstructive processes like a tension pneumothorax or hemothorax."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-stepoffs",
                        label: "step-offs",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient's neck exam was normal, but remember, a palpable step-off suggests a fracture."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "chest",
                label: "examine the patient's chest",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["The presence and quality of breath sounds can clue you into underlying problems (e.g., pneumothorax, asthma, fluid build-up in the lungs). Additionally, this patient had diminished sounds on the right, suggesting a possible pneumothorax or hemothorax.", "Inspecting the shoulders for discoloration symmetry or deformity may identify dislocation or fracture of the shoulder (clavicle, humerus and scapula).", "This patient had a 1 inch wound to the R chest at the sixth intercostal space with minor external bleeding."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-check-breath-sounds",
                        label: "Listen for breath sounds",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The presence and quality of breath sounds can clue you into underlying problems (e.g., pneumothorax, asthma, fluid build-up in the lungs).", "This patient had diminished sounds on the right, suggesting a possible pneumothorax or hemothorax."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                       },
                       {
                        id: "assessment-inspect-shoulders",
                        label: "Inspect and palpate shoulders",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Inspecting the shoulders for discoloration symmetry or deformity may identify dislocation or fracture of the shoulder (clavicle, humerus and scapula)"],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "assessment-inspect-clavicles",
                        label: "Inspect and palpates clavicles",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The clavicles should be inspected for any evidence of fractures. Though rare, clavicle fractures can cause a pneumothorax. This patient’s clavicles were normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-palpates-anterior-thorax",
                        label: "Inspect and palpate anterior thorax for injury, crepitation, and paradoxial motion (or flail segments)",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["This patient had a 1 inch wound to the R chest at the sixth intercostal space with minor external bleeding.", "If the patient had had anterior thorax and rib crepitation, you should suspect air outside of the lungs, which would raise your suspicion for a pneumothorax.", "Recall that paradoxical chest wall motion suggests flail chest, where multiple rib fractures lead to an unstable portion of the chest wall."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {id: "inspects-chest-injury",
                           feedbackAbsent: [""],
                           subActions: [],},
                          {id: "inspects-palpates-anterior-thorax-crepitation",
                           feedbackAbsent: ["If the patient had had anterior thorax and rib crepitation, you should suspect air outside of the lungs, which would raise your suspicion for a pneumothorax."],
                           subActions: []},
                          {id: "inspects-anterior-thorax-paradoxical-motion",
                           feedbackAbsent: ["Recall that paradoxical chest wall motion suggests flail chest, where multiple rib fractures lead to an unstable portion of the chest wall."],
                           subActions: []},
                        ],
                      },
                ],
              },
              {
                id: "abdomen-pelvis",
                label: "examine the patient's abdomen",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["A distended or rigit abdomen suggests trauma and internal bleeding. Fortunately, the patient's abdominal exam was normal."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "inspects-abdomen-injury",
                        label: "inspect and palpate for injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: [""],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-palpates-abdomen-distension",
                        label: "inspect and palpate for distension",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A distended abdomen suggests trauma and internal bleeding. Fortunately, the patient's abdominal exam was normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-palpates-abdomen-rigidity",
                        label: "inspect and palpate for rigidity",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["A rigid abdomen suggests trauma and internal bleeding. Fortunately, the patient's abdominal exam was normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "assess-pelvis",
                label: "examine the patient's pelvis",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Be sure to do a thorough exam of the pelvis as the pelvis can be a source of life-threatening bleeding. The presence of blood or other fluids may indicate urethral, vaginal or rectal injury.Fortunately, the patient's pelvis exam was normal."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "assessment-checks-pelvis",
                        label: "Inspect and compress the pelvis",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Be sure to do a thorough exam of the pelvis as the pelvis can be a source of life-threatening bleeding. Fortunately, the patient's pelvis exam was normal."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "inspects-genitalia-perineum-blood",
                        label: "Inspect the genitalia/perineum for blood and other fluids.",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["The presence of blood or other fluids may indicate urethral, vaginal or rectal injury."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "extremities",
                label: "examine the patient's upper and lower extremities",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Extremity pulses may be weak or non-palpable if your patient is in shock. This patient had normal extremity pulses.", "Diminished or absent mobility may suggest spinal cord or other nervous system injury. This patient was moving his extremities normally.", "Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "inspects-extremities-injury",
                        label: "Inspect and palpate each extremity for injury",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["You would have observed superficial lacerations to the hands."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-injury",
                            feedbackAbsent: ["You would have observed superficial lacerations to the hands."],
                            subActions: []},
                           {id: "inspects-left-arm-injury",
                            feedbackAbsent: ["You would have observed superficial lacerations to the hands."],
                            subActions: []},
                           {id: "inspects-right-leg-injury",
                            subActions: []},
                           {id: "inspects-left-leg-injury",
                            subActions: []},
                        ],
                      },
                      {
                        id: "inspects-extremities-pulse",
                        label: "Inspect and palpate each extremity for pulse",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Extremity pulses may be weak or non-palpable if your patient is in shock. This patient had normal extremity pulses"],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-pulse",
                            feedbackAbsent: ["Extremity pulses may be weak or non-palpable if your patient is in shock. This patient had normal extremity pulses"],
                            subActions: []},
                           {id: "inspects-left-arm-pulse",
                            feedbackAbsent: ["Extremity pulses may be weak or non-palpable if your patient is in shock. This patient had normal extremity pulses"],
                            subActions: []},
                           {id: "inspects-right-leg-pulse",
                            feedbackAbsent: ["Extremity pulses may be weak or non-palpable if your patient is in shock. This patient had normal extremity pulses"],
                            subActions: []},
                           {id: "inspects-left-leg-pulse",
                            feedbackAbsent: ["Extremity pulses may be weak or non-palpable if your patient is in shock. This patient had normal extremity pulses"],
                            subActions: []},
                        ],
                      },
                      {
                        id: "inspects-extremities-motor-function",
                        label: "Inspect and palpate each extremity for motor function",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Diminished or absent mobility may suggest spinal cord or other nervous system injury. This patient was moving his extremities normally."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-motor-ability",
                            feedbackAbsent: ["Diminished or absent mobility may suggest spinal cord or other nervous system injury. This patient was moving his extremities normally."],
                            subActions: []},
                           {id: "inspects-left-arm-motor-ability",
                            feedbackAbsent: ["Diminished or absent mobility may suggest spinal cord or other nervous system injury. This patient was moving his extremities normally."],
                            subActions: []},
                           {id: "inspects-right-leg-motor-ability",
                            feedbackAbsent: ["Diminished or absent mobility may suggest spinal cord or other nervous system injury. This patient was moving his extremities normally."],
                            subActions: []},
                           {id: "inspects-left-leg-motor-ability",
                            feedbackAbsent: ["Diminished or absent mobility may suggest spinal cord or other nervous system injury. This patient was moving his extremities normally."],
                            subActions: []},
                        ],
                      },
                      {
                        id: "inspects-extremities-sensory-function",
                        label: "Inspect and palpate each extremity for sensory function",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          { id: "inspects-right-arm-sensory-function",
                            feedbackAbsent: ["Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                            subActions: []},
                           {id: "inspects-left-arm-sensory-function",
                            feedbackAbsent: ["Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                            subActions: []},
                           {id: "inspects-right-leg-sensory-function",
                            feedbackAbsent: ["Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                            subActions: []},
                           {id: "inspects-left-leg-sensory-function",
                            feedbackAbsent: ["Diminished or absent sensation may suggest spinal cord or other nervous system injury. This patient's sensation was intact in his extremities."],
                            subActions: []},
                        ],
                      }, 
                ],
              },
              {
                id: "posterior",
                label: "inspect and palpate the posterior thorax, lumbar, and buttocks for injury and step-offs",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["You will need a partner to help you roll the patient safely in order to do a thorough exam of the posterior thorax, lumbar, and buttocks for injury and step-offs. A palpable step-off, which the patient did not have, would suggest a fracture."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [
                    { 
                      id: "inspects-posterior-other-injury",
                      label: "injury",
                      feedbackAbsent: [""],
                      subActions: []
                    },
                    {
                      id: "inspects-posterior-step-offs",
                      labe: "step-offs",
                      feedbackAbsent:["A palpable step-off, which the patient did not have, would suggest a fracture."],
                      subActions: []
                    },
                ],
              },
          ],
        },
        {
          id: "ongoing-mgmt-plan",
          label: "Continue to provide adequate prehospital care until arriving at the receiving facility",
          type: ACTION_TYPES.REQ,
          feedbackAbsent: ["It is important to reassess your patient after every intervention and every clinical change. This includes repeating the primary survey, secondary survey, and vital signs. A critically ill patient should be reassessed more frequently to ensure he or she is still stable--approximately every 5 minutes."],
          feedbackOutOfOrder: [""],
          feedbackErrors: [""],
          examineIfAbsent: false,
          examineIfErrors: false,
          promptsIfAbsent: "",
          promptsIfErrors: "",
          subActionsList: true,
          subActions: [
            {
                id: "intv-apply-sterile-dressings",
                label: "Apply sterile dressings to lacerations over the ear and on hands",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Once the patient has been stabilized, dress his superficial wounds with sterile dressings."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-place-on-immobilization-device-or-stretcher",
                label: "Place the patient on a transfer device, taking care not to compress puncture wounds and to move him to the ambulance with minimal spinal motion.",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["Since this patient has a penetrating chest wound, you need to be careful while strapping him onto a transfer or immobilization device, since doing so may cause further injury.  A scoop stretcher would be an appropriate way to safely transfer the patient.  If unavailable, a longboard or other device could be used (with straps carefully placed) and then removed once the patient is on the stretcher."],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["Since this patient has a penetrating chest wound, you need to be careful while strapping him onto a transfer or immobilization device, since doing so may cause further injury.  A scoop stretcher would be an appropriate way to safely transfer the patient.  If unavailable, a longboard or other device could be used (with straps carefully placed) and then removed once the patient is on the stretcher.", "In addition, this patient should be kept in a supine position. Remember that shock occurs when the body is unable to adequately perfuse the body's vital organs and periphery. When a patient is kept upright while in shock, cerebral perfusion is further hindered by gravity. Lying the patient down takes gravity out of the equation."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [
                  {
                    id: "intv-place-on-immobilization-device",
                    label: "Place the patient on a transfer device, taking care not to compress puncture wounds and to move him to the ambulance with minimal spinal motion.",
                    type: ACTION_TYPES.REQ,
                    feedbackAbsent: ["Since this patient has a penetrating chest wound, you need to be careful while strapping him onto a transfer or immobilization device, since doing so may cause further injury.  A scoop stretcher would be an appropriate way to safely transfer the patient.  If unavailable, a longboard or other device could be used (with straps carefully placed) and then removed once the patient is on the stretcher."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: ["Since this patient has a penetrating chest wound, you need to be careful while strapping him onto a transfer or immobilization device, since doing so may cause further injury.  A scoop stretcher would be an appropriate way to safely transfer the patient.  If unavailable, a longboard or other device could be used (with straps carefully placed) and then removed once the patient is on the stretcher.", "In addition, this patient should be kept in a supine position. Remember that shock occurs when the body is unable to adequately perfuse the body's vital organs and periphery. When a patient is kept upright while in shock, cerebral perfusion is further hindered by gravity. Lying the patient down takes gravity out of the equation."],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: [],
                  },
                  {
                    id: "intv-place-directly-on-stretcher",
                    label: "Place patient directly on stretcher",
                    type: ACTION_TYPES.ALT,
                    alternativeToIntv: "intv-place-on-immobilization-device",
                    feedbackAbsent: ["Since this patient has a penetrating chest wound, you need to be careful while strapping him onto a transfer or immobilization device, since doing so may cause further injury.  A scoop stretcher would be an appropriate way to safely transfer the patient.  If unavailable, a longboard or other device could be used (with straps carefully placed) and then removed once the patient is on the stretcher."],
                    feedbackOutOfOrder: [""],
                    feedbackErrors: ["Since this patient has a penetrating chest wound, you need to be careful while strapping him onto a transfer or immobilization device, since doing so may cause further injury.  A scoop stretcher would be an appropriate way to safely transfer the patient.  If unavailable, a longboard or other device could be used (with straps carefully placed) and then removed once the patient is on the stretcher.", "In addition, this patient should be kept in a supine position. Remember that shock occurs when the body is unable to adequately perfuse the body's vital organs and periphery. When a patient is kept upright while in shock, cerebral perfusion is further hindered by gravity. Lying the patient down takes gravity out of the equation."],
                    examineIfAbsent: false,
                    examineIfErrors: false,
                    promptsIfAbsent: "",
                    promptsIfErrors: "",
                    subActionsList: false,
                    subActions: []
                  },
                ],
              },
              {
                id: "intv-spinal-immobilization-technique-attach-cid",
                label: "apply padding (e.g., CID, towels) to keep head inline with spine",
                type: ACTION_TYPES.OPT,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "Reassess the patient",
                label: "Reassess the patient",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: true,
                subActions: [
                    {
                        id: "reassess-vitals-plus-frequency",
                        label: "Recheck vital signs every 5 minutes",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["Vitals should be reassessed after every intervention and every clinical change, and a critically ill patient should be reassessed every 5 minutes since their condition could deteriorate rapidly. In particular, in this scenario, you should have reassessed BP, P, R, and SpO2 to ensure that the patient's breathing was adequately managed."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: ["Vitals should be reassessed after every intervention and every clinical change, and a critically ill patient should be reassessed every 5 minutes since their condition could deteriorate rapidly. In particular, in this scenario, you should have reassessed BP, P, R, and SpO2 to ensure that the patient's breathing was adequately managed."],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [
                          {
                            id: "reassess-vital-signs",
                            label: "Recheck vital signs every 5 minutes",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["In this scenario, you should have reassessed BP, P, R, and SpO2 to ensure that the patient's breathing was adequately managed."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: ["In this scenario, you should have reassessed BP, P, R, and SpO2 to ensure that the patient's breathing was adequately managed."],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [], 
                          },
                          {
                            id: "decision-vitals-frequency",
                            label: "decision vitals frequency",
                            type: ACTION_TYPES.REQ,
                            feedbackAbsent: ["Vitals should be reassessed after every intervention and every clinical change, and a critically ill patient should be reassessed every 5 minutes since their condition could deteriorate rapidly."],
                            feedbackOutOfOrder: [""],
                            feedbackErrors: ["Vitals should be reassessed after every intervention and every clinical change, and a critically ill patient should be reassessed every 5 minutes since their condition could deteriorate rapidly."],
                            examineIfAbsent: false,
                            examineIfErrors: false,
                            promptsIfAbsent: "",
                            promptsIfErrors: "",
                            subActionsList: false,
                            subActions: [], 
                          },
                        ],
                      },
                      {
                        id: "repeat-primary-survey",
                        label: "Repeat the Primary Survey",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["It is important to reassess your patient after every intervention and every clinical change, which includes the primary survey. For example, a patient with a neck injury whose breathing was initially non-labored may develop stridor as the airway becomes swollen."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                      {
                        id: "repeat-secondary-survey",
                        label: "Repeat the Secondary Survey",
                        type: ACTION_TYPES.REQ,
                        feedbackAbsent: ["It is important to reassess your patient after every intervention and every clinical change, which includes your secondary survey. For example, a patient may lose their pulses if you initially failed to notice and treat a source of hemorrhage."],
                        feedbackOutOfOrder: [""],
                        feedbackErrors: [""],
                        examineIfAbsent: false,
                        examineIfErrors: false,
                        promptsIfAbsent: "",
                        promptsIfErrors: "",
                        subActionsList: false,
                        subActions: [],
                      },
                ],
              },
              {
                id: "intv-establish-iv",
                label: "Establish at least one large bore IV en route",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["All trauma patients should have at least one, ideally two, large bore IVs (i.e., 14 or 16 gauge) placed, even if they do not require fluid resuscitation."],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["With a critical patient, you should ideally establish two large bore IVs (i.e., 14 or 16 gauge) while en route to the receiving facility, so as not to delay transfer to definitive care.  While this patient does not require fluid resuscitation at this time, you want to ensure you are prepared should this change."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-contact-receiving-facility",
                label: "Provide a patient report and ETA to the receiving facility",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["The hospital requires notification for all incoming patients 5 -15 prior to arrival, which is especially important for critical patients as the receiving team needs time to prepare equipment and space."],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-control-shock-technique-keep-patient-warm",
                label: "keep the patient warm, using some of the following methods: a) applying heat packs to the patient's groin or armpits, b) covering the patient with blankets, c) turning the heat up in the ambulance.",
                type: ACTION_TYPES.REQ,
                feedbackAbsent: ["This patient is hypotensive with a fast heart rate, suggesting possible shock. Patients in shock have trouble maintaining their body temperatures, so they should be kept warm. It is important to keep this patient warm because hypothermia can cause additional problems or worsening of the patient's current problems, including worsening hypotension, arrhythmias, or even cardiac arrest."],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["You chose a warming method that isn't appropriate in this scenario. This patient should be kept warm by one or more of the following methods: apply heat packs to the patient's groin or armpits, cover the patient with blankets, or turn the heat up in the ambulance."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "intv-control-shock-technique-administer-iv-boluses", /* optional intervention */
                label: "Bolus normal saline or Lactated Ringers to a maintain BP of 90mm Hg systolic en route, if time allows",
                type: ACTION_TYPES.OPT,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["While it is true the patient was hypotensive, the reason for his hypotension was because of obstructive shock, not hypovolemia. Appropriate management of the patient's tension pneumothorax would allow resumption of adequate cardiac output, resulting in an improved BP and exam. This patient does have a penetrating chest wound, however, so should he remain hypotensive after you successfully treated his tension pneumothorax, you should consider hemorrhagic shock; at this point, fluid resuscitation may be appropriate."],
                examineIfAbsent: false,
                examineIfErrors: true,
                promptsIfAbsent: "",
                promptsIfErrors: "Thought that hypotension should be treated with fluid boluses regardless of etiology",
                subActionsList: false,
                subActions: [],
              },
              {
                id: "intv-contact-medical-command", /* optional intervention */
                label: "Contacted Medical Command",
                type: ACTION_TYPES.OPT,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: [""],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-walk-patient-to-ambulance", /* not indicated intervention */
                label: "Walk patient to ambulance",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["This patient should be transferred given his altered mental status, hypotension and penetrating chest wound."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-splint-fracture", /* not indicated intervention */
                label: "Splint fractures",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have any fractures."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-sling", /* not indicated intervention */
                label: "Apply sling",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["This patient did not have any injuries to the upper extremities."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-bulky-dressing ", /* not indicated intervention */
                label: "Bulky dressing",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["This patient did not have any injuries that needed to be stabilized by a bulky dressing."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-prepare-amputated-part", /* not indicated intervention */
                label: "Prepare amputated part for transport",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["The patient did not have an amputation."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
              {
                id: "intv-prepare-and-administer-pain-nausea-vomiting-medications", /* not indicated intervention */
                label: "Prepare and administer medications",
                type: ACTION_TYPES.NOTIND,
                feedbackAbsent: [""],
                feedbackOutOfOrder: [""],
                feedbackErrors: ["A pain level for this patient is not available, and there is no evidence of nausea, vomiting, or need for sedation."],
                examineIfAbsent: false,
                examineIfErrors: false,
                promptsIfAbsent: "",
                promptsIfErrors: "",
                subActionsList: false,
                subActions: []
              },
          ],
        },     
    ],
},
}
